import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Transaction, TransactionFilters, GetTransactionsResponse } from '../../types';
import apiService from '../../services/api';

interface TransactionsState {
  transactions: Transaction[];
  selectedTransaction: Transaction | null;
  statistics: {
    totalTransactions: number;
    totalAmount: number;
    deposits: number;
    withdrawals: number;
    wins: number;
    transfers: number;
  };
  filters: TransactionFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  loading: boolean;
  error: string | null;
}

const initialState: TransactionsState = {
  transactions: [],
  selectedTransaction: null,
  statistics: {
    totalTransactions: 0,
    totalAmount: 0,
    deposits: 0,
    withdrawals: 0,
    wins: 0,
    transfers: 0,
  },
  filters: {},
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
  loading: false,
  error: null,
};

export const fetchTransactions = createAsyncThunk(
  'transactions/fetchTransactions',
  async ({ page, limit, filters }: { page: number; limit: number; filters?: TransactionFilters }) => {
    const response = await apiService.getTransactions(page, limit, filters);
    return response;
  }
);

export const fetchTransaction = createAsyncThunk(
  'transactions/fetchTransaction',
  async (transactionId: string) => {
    const response = await apiService.getTransaction(transactionId);
    return response;
  }
);

const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedTransaction: (state, action: PayloadAction<Transaction | null>) => {
      state.selectedTransaction = action.payload;
    },
    setFilters: (state, action: PayloadAction<TransactionFilters>) => {
      state.filters = action.payload;
      state.pagination.page = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.pagination.page = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.transactions = action.payload.data;
          state.pagination = {
            page: action.payload.pagination.page,
            limit: action.payload.pagination.limit,
            total: action.payload.pagination.total,
            pages: action.payload.pagination.pages,
          };
          
          // Calculate statistics from transactions (filter out null user transactions)
          const validTransactions = action.payload.data.filter((t: Transaction) => t.user !== null);
          const totalAmount = validTransactions.reduce((sum: number, t: Transaction) => sum + t.amount, 0);
          const deposits = validTransactions.filter((t: Transaction) => t.type === 'deposit').length;
          const withdrawals = validTransactions.filter((t: Transaction) => t.type === 'withdraw').length;
          const wins = validTransactions.filter((t: Transaction) => t.type === 'win').length;
          const transfers = validTransactions.filter((t: Transaction) => t.type === 'transfer').length;
          
          state.statistics = {
            totalTransactions: validTransactions.length,
            totalAmount,
            deposits,
            withdrawals,
            wins,
            transfers,
          };
        }
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch transactions';
      })
      .addCase(fetchTransaction.fulfilled, (state, action) => {
        state.selectedTransaction = action.payload;
      });
  },
});

export const { clearError, setSelectedTransaction, setFilters, clearFilters, setPage } = transactionsSlice.actions;
export default transactionsSlice.reducer; 